import { createApi } from "@reduxjs/toolkit/query/react";
import * as logger from "common/logger";
import * as time from "common/time";
import * as config from "config";
import * as FileSaver from "file-saver";
import * as utils from "utils";

const baseQuery = async (args, api, extraOptions) => {
  try {
    const response = await utils.authenticatedFetch(
      config.endpoints.docGen(args.url),
      args.payload || {},
      args.impersonateCompanyId
    );
    return {
      data: await response.json(),
    };
  } catch (e) {
    return {
      error: String(e),
    };
  }
};

// Flatten anything thrown by authenticatedFetch into the shape components
// read: a HandledError carries {name, description, body}, a plain Error only
// {name, message}. `body` is the structured payload of a ClientException
// (for example a template validation report) or null.
export const toStructuredError = (e) => ({
  type: e.type ?? e.name ?? "UNKNOWN",
  message: e.description ?? e.message ?? String(e),
  body: e.body ?? null,
});

const RENDER_WARNINGS_HEADER = "x-mmm-render-warnings";

const parseRenderWarningsHeader = (response) => {
  const raw = response.headers.get(RENDER_WARNINGS_HEADER);
  if (!raw) {
    return [];
  }
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    logger.error(`Could not parse ${RENDER_WARNINGS_HEADER} header: ${e}`);
    return [];
  }
};

export const docGenApi = createApi({
  reducerPath: "docGenApi",
  baseQuery: baseQuery,
  endpoints: (builder) => ({
    getTemplateSummary: builder.mutation({
      queryFn: async ({ templateId, templateForm, content }) => {
        if (templateForm.includes("JINJA")) {
          const response = await baseQuery({
            url: "get-template-summary",
            payload: {
              contentType: "application/json",
              method: "POST",
              body: JSON.stringify({
                templateId,
                submission: content?.submission ?? {},
                layerId: content?.layerId,
                policyId: content?.policyId,
              }),
            },
          });
          if (response.error) {
            return { error: response.error };
          }
          return { data: response.data };
        } else {
          return { data: { evaluations: [], erroringTemplates: [] } };
        }
      },
    }),
    listTemplates: builder.query({
      query: () => ({
        url: "list-templates",
      }),
    }),
    fillTemplate: builder.mutation({
      queryFn: async ({
        templateId,
        templateForm,
        emailTargetId,
        content,
        filename,
        endorsementIndex,
        useOriginalTemplate = false,
        wordDocumentOnly = false,
      }) => {
        try {
          const makeEndpoint = () => {
            if (
              [
                "WORD_JINJA",
                "WORD_JINJA_ENDORSEMENT",
                "ENDORSEABLE_WORD_JINJA",
                "PDF_ENDORSEMENT",
                "ENDORSABLE_PDF",
              ].includes(templateForm)
            ) {
              return "fill-template";
            } else if (templateForm === "WORD") {
              return "fill-word-template";
            } else if (templateForm === "EXCEL") {
              return "fill-excel-template";
            } else {
              logger.error(`Unknown template form: ${templateForm}`);
              return "fill-template";
            }
          };

          const makeBody = () => {
            if (
              [
                "WORD_JINJA",
                "WORD_JINJA_ENDORSEMENT",
                "ENDORSEABLE_WORD_JINJA",
                "PDF_ENDORSEMENT",
                "ENDORSABLE_PDF",
              ].includes(templateForm)
            ) {
              return {
                templateId,
                emailTargetId,
                submission: content?.submission ?? {},
                layerId: content?.layerId,
                policyId: content?.policyId,
                endorsementIndex,
                useOriginalTemplate,
                wordDocumentOnly,
              };
            } else {
              return {
                templateId,
                emailTargetId,
                data: {
                  content,
                },
              };
            }
          };

          let response = await utils.authenticatedFetch(
            config.endpoints.docGen(makeEndpoint()),
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify(makeBody()),
            }
          );

          let documentKey = null;
          let warnings = [];

          if (response.headers.get("Content-Type") === "application/json") {
            const { id } = await response.json();
            while (true) {
              response = await utils.authenticatedFetch(
                config.endpoints.docGen("retrieve-fill-template"),
                {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({ id }),
                }
              );
              const responseJson = await response.json();
              if (
                responseJson.status === "pending" ||
                responseJson.status === "running"
              ) {
                await time.sleep(1000);
                continue;
              } else if (responseJson.status === "succeeded") {
                documentKey = responseJson.documentKey;
                warnings = responseJson.warnings ?? [];
                response = await fetch(responseJson.presignedUrl);
                break;
              } else {
                return {
                  error: responseJson.errorDetail ?? {
                    code: "DOCUMENT_GENERATION_FAILED",
                    message: responseJson.error ?? "Unknown error",
                  },
                };
              }
            }
          } else {
            // Synchronous document download: the body is the file, so any
            // render warnings travel in a header.
            warnings = parseRenderWarningsHeader(response);
          }
          const blob = await response.blob();
          FileSaver.saveAs(blob, filename);
          return { data: { status: "succeeded", documentKey, warnings } };
        } catch (e) {
          logger.exception(e);
          return {
            error: toStructuredError(e),
          };
        }
      },
    }),

    validateTemplate: builder.mutation({
      query: ({
        templateId,
        fileId,
        title,
        variables,
        imageReplacements,
        impersonateCompanyId,
      }) => ({
        url: "validate-template",
        payload: {
          method: "post",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            templateId,
            fileId,
            title,
            variables,
            imageReplacements,
          }),
        },
        impersonateCompanyId,
      }),
    }),

    getTemplateContextCatalogue: builder.query({
      query: ({ impersonateCompanyId } = {}) => ({
        url: "template-context-catalogue",
        impersonateCompanyId,
      }),
    }),

    getTemplateOverrides: builder.query({
      query: ({ submissionId }) => ({
        url: "get-template-overrides",
        payload: {
          contentType: "application/json",
          method: "POST",
          body: JSON.stringify({
            submissionId,
          }),
        },
      }),
    }),

    getTemplateOverride: builder.query({
      query: ({ submissionId, layerId, templateId }) => ({
        url: "get-template-override",
        payload: {
          contentType: "application/json",
          method: "POST",
          body: JSON.stringify({
            submissionId,
            layerId,
            templateId,
          }),
        },
      }),
    }),

    setTemplateOverride: builder.mutation({
      query: ({ submissionId, layerId, templateId, overrideFileId }) => ({
        url: "set-template-override",
        payload: {
          contentType: "application/json",
          method: "POST",
          body: JSON.stringify({
            submissionId,
            layerId,
            templateId,
            overrideFileId,
          }),
        },
      }),
    }),

    removeTemplateOverride: builder.mutation({
      query: ({ submissionId, layerId, templateId }) => ({
        url: "remove-template-override",
        payload: {
          contentType: "application/json",
          method: "POST",
          body: JSON.stringify({
            submissionId,
            layerId,
            templateId,
          }),
        },
      }),
    }),

    resolveTemplateFile: builder.query({
      query: ({ submissionId, layerId, templateId }) => ({
        url: "resolve-template-file",
        payload: {
          contentType: "application/json",
          method: "POST",
          body: JSON.stringify({
            submissionId,
            layerId,
            templateId,
          }),
        },
      }),
    }),

    downloadGeneratedDocumentFile: builder.mutation({
      queryFn: async ({ documentKey, filename }) => {
        try {
          const response = await utils.authenticatedFetch(
            config.endpoints.docGen("download-generated-document-file"),
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify({ documentKey }),
            }
          );
          const { presignedUrl } = await response.json();

          const fileResponse = await fetch(presignedUrl);
          const blob = await fileResponse.blob();
          FileSaver.saveAs(blob, filename);

          return { data: "succeeded" };
        } catch (e) {
          logger.exception(e);
          return {
            error: String(e),
          };
        }
      },
    }),
    convertFileToPdf: builder.mutation({
      query: ({ fileId }) => ({
        url: "convert-file-to-pdf",
        payload: {
          body: JSON.stringify({ fileId }),
          method: "post",
          headers: {
            "Content-Type": "application/json",
          },
        },
      }),
    }),
    createTemplateRequest: builder.mutation({
      query: ({
        requestType,
        formNumber,
        templateName,
        title,
        description,
        priority,
        status,
        selectedFields,
        customFields,
        placements,
        annotations,
        s3FileId,
        fileType,
        batchId,
        templateId,
      }) => ({
        url: "create-template-request",
        payload: {
          body: JSON.stringify({
            requestType,
            formNumber,
            templateName,
            title,
            description,
            priority,
            status,
            selectedFields,
            customFields,
            placements,
            annotations,
            s3FileId,
            fileType,
            batchId,
            templateId,
          }),
          method: "post",
          headers: {
            "Content-Type": "application/json",
          },
        },
      }),
    }),
    listTemplateRequests: builder.query({
      query: ({ includeAll = false, impersonateCompanyId } = {}) => ({
        url: "list-template-requests",
        payload: {
          body: JSON.stringify({ includeAll }),
          method: "post",
          headers: {
            "Content-Type": "application/json",
          },
        },
        impersonateCompanyId,
      }),
    }),
    retrieveTemplateRequest: builder.query({
      query: ({ requestId, impersonateCompanyId }) => ({
        url: "retrieve-template-request",
        payload: {
          body: JSON.stringify({ requestId }),
          method: "post",
          headers: {
            "Content-Type": "application/json",
          },
        },
        impersonateCompanyId,
      }),
    }),
    updateTemplateRequest: builder.mutation({
      // A queryFn rather than a query so that a TEMPLATE_VALIDATION_FAILED
      // refusal reaches the component with its report in error.body.
      queryFn: async ({
        requestId,
        status,
        priority,
        descriptionMessage,
        force = false,
        impersonateCompanyId,
      }) => {
        try {
          const response = await utils.authenticatedFetch(
            config.endpoints.docGen("update-template-request"),
            {
              body: JSON.stringify({
                requestId,
                status,
                priority,
                descriptionMessage,
                force,
              }),
              method: "post",
              headers: {
                "Content-Type": "application/json",
              },
            },
            impersonateCompanyId
          );
          return { data: await response.json() };
        } catch (e) {
          logger.exception(e);
          return { error: toStructuredError(e) };
        }
      },
    }),
    deleteTemplateRequest: builder.mutation({
      query: ({ requestId, impersonateCompanyId }) => ({
        url: "delete-template-request",
        payload: {
          body: JSON.stringify({ requestId }),
          method: "post",
          headers: {
            "Content-Type": "application/json",
          },
        },
        impersonateCompanyId,
      }),
    }),
  }),
});

export const {
  useFillTemplateMutation,
  useListTemplatesQuery,
  useGetTemplateSummaryMutation,
  useGetTemplateOverridesQuery,
  useGetTemplateOverrideQuery,
  useSetTemplateOverrideMutation,
  useRemoveTemplateOverrideMutation,
  useResolveTemplateFileQuery,
  useDownloadGeneratedDocumentFileMutation,
  useConvertFileToPdfMutation,
  useCreateTemplateRequestMutation,
  useListTemplateRequestsQuery,
  useRetrieveTemplateRequestQuery,
  useUpdateTemplateRequestMutation,
  useDeleteTemplateRequestMutation,
  useValidateTemplateMutation,
  useGetTemplateContextCatalogueQuery,
} = docGenApi;
